/**
 * My Favotrip customer-portal API client.
 *
 * The backend at api.favotrip.nl sets the `ft_customer_session` cookie
 * with `Domain=.favotrip.nl`, so the browser automatically sends it on
 * every cross-subdomain fetch as long as we set `credentials: 'include'`.
 *
 * All helpers throw on non-2xx so callers can react with try/catch.
 * 401s indicate a missing or invalid cookie and the UI should redirect
 * to /login.
 *
 * favotrip.be (NEXT_PUBLIC_LUXE_BE): the session cookie lives on
 * Domain=.favotrip.be there, so calls go same-origin through
 * /api/klant/api/... instead (see requestTarget below).
 *
 * favotrip.de (flag ING_DE_LIVE): the session cookie is host-only on the .de
 * host, so the ING entrance calls go same-origin through /api/klant-de/...
 * (see requestTarget); every other call behaves as on main. With only
 * Favotrip Exclusive on .de (LUXE_DE_PUBLIEK_LIVE), /customer/me and logout
 * take that same route, because the members' login sets the same host-only
 * session.
 */

import { KLANT_API_PAD, KLANT_SESSIE_PAD, klantPortalInBrowser } from "@/lib/klant-sessie-be";
import { KLANT_DE_API_PAD, ingDeAanInBrowser, klantDeRoute } from "@/lib/luxe/ing-de";
import { exclusiveDeLedenAanInBrowser } from "@/lib/luxe/exclusive-de-leden";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.favotrip.nl";

/** Error thrown by customerApi on a non-2xx response; carries the HTTP status and the parsed payload. */
export class CustomerApiError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = "CustomerApiError";
    this.status = status;
    this.payload = payload;
  }
}

// favotrip.be (vlag aan): de sessiecookie staat op Domain=.favotrip.be en gaat
// dus niet mee naar api.favotrip.nl. Daar loopt elke aanroep met sessie via de
// same-origin doorgeefroute (src/app/api/klant/api/[...pad]/route.js). Op .nl
// en met de vlag uit: api.favotrip.nl met credentials:'include', als op main.
// `anoniem` (de magic-link-aanvragen) gaat hier rechtstreeks: geen sessie
// nodig, en niet in de allowlist van die route. Uitzondering: op .de met
// ING_DE_LIVE aan gaan de magic-link-aanvragen uit KLANT_DE_ROUTES via de
// .de-proxy (hieronder), ook als ze `anoniem` zijn.
//
// favotrip.de (regie 25-10-2026, ING-ingang; schakelaar ING_DE_LIVE in
// src/lib/luxe/ing-de-flags.js): de sessiecookie staat daar host-only op de
// .de-host. Alleen de verzoeken van de ING-ingang (de allowlist KLANT_DE_ROUTES:
// /customer/me, uitloggen, de magic-link-aanvragen, /exclusive-toegang/* en
// /exclusive-ing/*) gaan daar via de same-origin proxy
// (src/app/api/klant-de/[...pad]/route.js), die bij een aanvraag de
// nonce-cookie zet en nonce en domein meestuurt. Alle andere klantpaden
// (boekingen, vouchers, favorieten, herinneringen, profiel wijzigen) en de
// schakelaar uit: zoals op main.
//
// Alleen Favotrip Exclusive op .de aan (LUXE_DE_PUBLIEK_LIVE aan, ING_DE_LIVE
// uit; fixronde 3 fe#1938): het aanmeldblok logt via dezelfde proxy en
// inlogroute in, dus ook dan staat de sessie host-only op .de. `me` en
// uitloggen (de regels met `exclusiveDe`) gaan dan ook via de proxy, anders
// herkent of wist de browser die sessie niet. Alle andere klantpaden: als op main.
function requestTarget(path, { anoniem = false, method = "GET" } = {}) {
  const deRoute = klantDeRoute(method, path);
  if (deRoute && (ingDeAanInBrowser() || (deRoute.exclusiveDe && exclusiveDeLedenAanInBrowser()))) {
    return { url: `${KLANT_DE_API_PAD}${path}`, credentials: "same-origin" };
  }
  if (!anoniem && klantPortalInBrowser()) {
    return { url: `${KLANT_API_PAD}${path}`, credentials: "same-origin" };
  }
  return { url: `${API_BASE_URL}${path}`, credentials: "include" };
}

async function request(path, init = {}, opts) {
  const target = requestTarget(path, { ...opts, method: init.method || "GET" });
  const res = await fetch(target.url, {
    ...init,
    credentials: target.credentials,
    headers: {
      "Content-Type": "application/json",
      ...(init.headers || {}),
    },
  });

  let payload = null;
  const text = await res.text();
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = text;
    }
  }

  if (!res.ok) {
    throw new CustomerApiError(
      payload?.message || `Request failed: ${res.status}`,
      res.status,
      payload,
    );
  }

  return payload;
}

/**
 * Auth hint (E2E QA 2026-06-10): the ft_customer_session cookie is
 * HttpOnly, so the FE cannot see whether a session exists. Header /
 * favorite / reminder components used to fire /customer/me on EVERY
 * pageview, producing 2-4 guaranteed 401 console errors per page for
 * anonymous visitors. We keep a non-authoritative localStorage hint:
 *   - set after any successful /customer/me (AccountShell, header);
 *   - cleared on 401 and on logout.
 * `meQuiet()` skips the network entirely when the hint is absent
 * (anonymous = the overwhelmingly common case) and single-flights +
 * briefly caches the call so one pageview costs at most one request.
 * The hint is a UI optimisation only, authorization stays fully
 * server-side; /account pages still do a real me() via AccountShell,
 * which also repairs a missing hint after a magic-link/OAuth login.
 */
const AUTH_HINT_KEY = "ft_customer_auth_hint";

function hasAuthHint() {
  try {
    return window.localStorage.getItem(AUTH_HINT_KEY) === "1";
  } catch {
    return false;
  }
}

/** Window event fired when the auth hint flips absent -> present (login). */
export const CUSTOMER_AUTH_EVENT = "ft:customer-auth";

function setAuthHint(on) {
  let becameAuthed = false;
  try {
    if (on) {
      becameAuthed = window.localStorage.getItem(AUTH_HINT_KEY) !== "1";
      window.localStorage.setItem(AUTH_HINT_KEY, "1");
    } else window.localStorage.removeItem(AUTH_HINT_KEY);
  } catch {}
  return becameAuthed;
}

// Called after a successful me(): on the absent -> present transition
// (Google / magic-link login lands on /account where AccountShell's me()
// is the first success), seed the meQuiet cache with the profile and tell
// always-mounted chrome (header) so it flips without a hard reload.
function onMeSuccess(profile) {
  const becameAuthed = setAuthHint(true);
  if (becameAuthed && typeof window !== "undefined") {
    meQuietCache = { promise: Promise.resolve(profile), ts: Date.now() };
    try {
      window.dispatchEvent(new Event(CUSTOMER_AUTH_EVENT));
    } catch {}
  }
}

const ME_QUIET_TTL_MS = 30_000;
let meQuietCache = null; // { promise, ts }

function meQuiet() {
  if (typeof window === "undefined" || !hasAuthHint()) {
    return Promise.resolve(null);
  }
  const now = Date.now();
  if (meQuietCache && now - meQuietCache.ts < ME_QUIET_TTL_MS) {
    return meQuietCache.promise;
  }
  const promise = request("/customer/me")
    .then((profile) => {
      setAuthHint(true);
      return profile;
    })
    .catch((err) => {
      meQuietCache = null;
      if (err instanceof CustomerApiError && err.status === 401) {
        setAuthHint(false);
        return null;
      }
      // Transient failure: treat as anonymous for this paint, retry later.
      return null;
    });
  meQuietCache = { promise, ts: now };
  return promise;
}

/** My Favotrip customer-portal API client (magic-link auth, profile, bookings). Sends the shared .favotrip.nl session cookie via credentials:'include'; throws CustomerApiError on non-2xx (401 means the UI should redirect to /login). */
export const customerApi = {
  // `bron`: eerste aanraking uit leesBron() (src/lib/aanmeld-bron.js);
  // undefined valt weg in JSON.stringify, dan gaat er niets mee.
  requestExclusiveMagicLink: ({ email, returnTo, turnstileToken, bron }) =>
    request("/customer/auth/exclusive/magic-link/request", {
      method: "POST",
      body: JSON.stringify({ email, returnTo, turnstileToken, bron }),
    }, { anoniem: true }),
  // `portal`: "be" op favotrip.be met NEXT_PUBLIC_LUXE_BE aan
  // (klantPortalFor in src/lib/klant-sessie-be.js); anders undefined, dan
  // valt hij weg in JSON.stringify en is de body gelijk aan main.
  requestMagicLink: ({ email, locale, returnTo, portal }) =>
    request("/customer/auth/magic-link/request", {
      method: "POST",
      body: JSON.stringify({ email, locale, returnTo, portal }),
    }, { anoniem: true }),

  logout: async () => {
    setAuthHint(false);
    meQuietCache = null;
    // favotrip.be (vlag aan): de sessiecookie staat daar op Domain=.favotrip.be,
    // gezet door de eigen BFF-route; alleen die kan hem ook wissen. Daarna nog
    // de backend-uitlog (via de doorgeefroute, die zonder cookie 401 geeft),
    // maar een fout daar houdt het uitloggen niet tegen.
    if (klantPortalInBrowser()) {
      const res = await fetch(KLANT_SESSIE_PAD, { method: "DELETE", credentials: "same-origin" });
      if (!res.ok) throw new CustomerApiError(`Request failed: ${res.status}`, res.status, null);
      return request("/customer/auth/logout", { method: "POST" }).catch(() => null);
    }
    return request("/customer/auth/logout", {
      method: "POST",
    });
  },

  /**
   * @param {{ signal?: AbortSignal }} [opts]
   * Pass an `AbortController.signal` to allow callers to cancel an
   * in-flight request on unmount / navigation. When aborted, fetch
   * throws an AbortError which callers must filter to avoid spurious
   * error states.
   */
  me: ({ signal } = {}) =>
    request("/customer/me", { signal }).then(
      (profile) => {
        onMeSuccess(profile);
        return profile;
      },
      (err) => {
        if (err instanceof CustomerApiError && err.status === 401) {
          setAuthHint(false);
          meQuietCache = null;
        }
        throw err;
      },
    ),

  /**
   * Hint-gated, single-flighted /customer/me. Resolves to the profile
   * when authenticated and to `null` when anonymous (no auth hint, 401,
   * or transient failure), it NEVER rejects on auth state. Use this in
   * always-mounted chrome (header, deal action buttons) so anonymous
   * pageviews stay network-silent. The optional signal is accepted for
   * caller symmetry but the underlying fetch is shared, so an abort of
   * one caller never cancels the others.
   */
  meQuiet: () => meQuiet(),

  updateMe: (patch, { signal } = {}) =>
    request("/customer/me", {
      method: "PATCH",
      body: JSON.stringify(patch),
      signal,
    }),

  requestAccountDeletion: ({ signal } = {}) =>
    request("/customer/me", {
      method: "DELETE",
      body: JSON.stringify({ confirm: "DELETE-MY-ACCOUNT" }),
      signal,
    }),

  bookings: ({ tab = "aankomend", pageSize = 25, signal } = {}) =>
    request(
      `/customer/bookings?tab=${encodeURIComponent(
        tab,
      )}&pageSize=${pageSize}`,
      { signal },
    ),

  bookingDetail: (reservationNumber, { signal } = {}) =>
    request(
      `/customer/bookings/${encodeURIComponent(reservationNumber)}`,
      { signal },
    ),

  // Travel documents for a booking. Returns { documents: [{ id, type,
  // label, filename, createdAt }] }; empty array when none yet, 404 when
  // the booking isn't this customer's (handled by the caller as
  // "unavailable", never surfaced raw).
  bookingDocuments: (reservationNumber, { signal } = {}) =>
    request(
      `/customer/bookings/${encodeURIComponent(reservationNumber)}/documents`,
      { signal },
    ),

  // Builds the URL to the authenticated download endpoint for one document.
  // Deliberately NOT a fetch: the endpoint 302-redirects to a short-lived
  // signed URL that streams the PDF, so we hand this to an <a href> and let
  // the browser navigate (the httpOnly .favotrip.nl session cookie tags
  // along and the redirect is followed natively). Running it through
  // request() would follow the 302 and try to JSON-parse the PDF.
  // On favotrip.be (flag on) the link is same-origin via the BFF route,
  // which forwards the .be cookie and passes only the https Location back.
  documentDownloadUrl: (reservationNumber, documentId) =>
    `${requestTarget("").url}/customer/bookings/${encodeURIComponent(
      reservationNumber,
    )}/documents/${encodeURIComponent(documentId)}/download`,

  vouchers: ({ signal } = {}) => request("/customer/vouchers", { signal }),

  reminders: ({ signal } = {}) =>
    request("/customer/reminders", { signal }),

  createReminder: ({ dealId, triggerType }, { signal } = {}) =>
    request("/customer/reminders", {
      method: "POST",
      body: JSON.stringify({ dealId, triggerType }),
      signal,
    }),

  deleteReminder: (id, { signal } = {}) =>
    request(`/customer/reminders/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal,
    }),

  favorites: ({ signal } = {}) =>
    request("/customer/favorites", { signal }),

  createFavorite: ({ kind, slug, displayName }, { signal } = {}) =>
    request("/customer/favorites", {
      method: "POST",
      body: JSON.stringify({ kind, slug, displayName }),
      signal,
    }),

  deleteFavorite: (id, { signal } = {}) =>
    request(`/customer/favorites/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal,
    }),
};
